import type { FilterObject } from '@moment/trading-types/filter-expression'
import type { RootState } from '@/app/store'
import type {
  PositionSort,
  PositionSortField,
  SortDirection,
} from '@/graphql/generated/schema'
import type { PageConfigRequest } from '@/types/api/page-config'
import type { ColumnId } from '@/types/enums/column-id'

import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit'
import { isEqual } from 'lodash'

import { updatePageConfig } from '@/legacy-api/page-config-slice'
import { defaultPageConfigs } from '@/reducers/defaults/page-configs'
import { FilterInteractionMode } from '@/types/enums/filter-interaction-mode'
import { Page } from '@/types/schema/page-configs'
import {
  type ColumnState,
  HOLDING_LIST_SCHEMA_VERSION,
  type HoldingListConfig,
} from '@/types/schema/page-configs/holding-list'
import { decodeFilter } from '@/utils/decode-filters'

interface HoldingListState {
  // All of these are not in the HoldingListPageQueryParams
  // They should be updated directly with dispatch calls
  config: HoldingListConfig
  configVersion: string
  defaultConfig: HoldingListConfig
  interactionMode: FilterInteractionMode
  // Pre-selected accounts for order entry with allocation details
  preSelectedAccountAllocations: {
    accountId: string
    momentAccountId?: string // Store the moment_account_id for order submission
    accountName: string
    quantity: number
    maxQuantity?: number // Maximum sellable quantity for this account
    sleeveId?: string // Store the sleeve_id for the position
  }[]

  // All of these are in the HoldingListPageQueryParams
  // They should not be updated directly with dispatch calls
  // since the useSyncQueryParamsToStore hook is used to update them
  selectedBond: string | null
  filters: FilterObject | null
  filterName: string | null
  quickFilter: FilterObject | null
  quickFilterName: string | null
  quickFilterId: string | null
  searchTerm: string | null
  sortBy: PositionSort[] | null
}

const initialState: HoldingListState = {
  config: defaultPageConfigs[Page.HoldingList],
  configVersion: HOLDING_LIST_SCHEMA_VERSION,
  defaultConfig: defaultPageConfigs[Page.HoldingList],
  selectedBond: null,
  filters: null,
  filterName: null,
  quickFilter: null,
  quickFilterName: null,
  quickFilterId: null,
  searchTerm: null,
  interactionMode: FilterInteractionMode.EXPLORING,
  sortBy: null,
  preSelectedAccountAllocations: [],
}

const holdingListSlice = createSlice({
  name: 'holdingList',
  reducerPath: 'holdingList',
  initialState,
  reducers: {
    initializeHoldingListConfig: handleInitializeHoldingListConfig,
    reset: handleReset,
    resetHoldingListConfig: handleResetHoldingListConfig,
    setHoldingListConfig: handleSetHoldingListConfig,
    updateColumnState: handleUpdateColumnState,
    updateColumnOrder: handleUpdateColumnOrder,
    updateColumnStates: handleUpdateColumnStates,
    selectedBond: handleSetSelectedBond,
    filters: handleSetFilters,
    filterName: handleSetFilterName,
    searchTerm: handleSetSearchTerm,
    quickFilter: handleSetQuickFilter,
    quickFilterName: handleSetQuickFilterName,
    quickFilterId: handleSetQuickFilterId,
    sortBy: handleSetSortBy,
    setPreSelectedAccountAllocations: handleSetPreSelectedAccountAllocations,
    clearPreSelectedAccountAllocations:
      handleClearPreSelectedAccountAllocations,
  },
})

function handleReset(state: HoldingListState) {
  Object.assign(state, initialState)
}

function handleInitializeHoldingListConfig(
  state: HoldingListState,
  action: PayloadAction<{ config: HoldingListConfig; version: string }>,
) {
  state.config = action.payload.config
  state.configVersion = action.payload.version
  state.defaultConfig = action.payload.config
}

function handleResetHoldingListConfig(state: HoldingListState) {
  state.config = state.defaultConfig
}

function handleSetHoldingListConfig(
  state: HoldingListState,
  action: PayloadAction<Partial<HoldingListConfig>>,
) {
  const updatedConfig = {
    ...state.config,
    ...action.payload,
  }
  state.config = updatedConfig
}

function handleUpdateColumnOrder(
  state: HoldingListState,
  action: PayloadAction<{
    colId: ColumnId
    toIndex: number
  }>,
) {
  const columnStateForId = state.config.columnState.find(
    (column) => column.colId === action.payload.colId,
  )
  if (!columnStateForId) {
    return
  }
  const updatedColumnStates = state.config.columnState.filter(
    (column) => column.colId !== action.payload.colId,
  )
  updatedColumnStates.splice(action.payload.toIndex, 0, columnStateForId)
  state.config.columnState = updatedColumnStates
}

function handleUpdateColumnState(
  state: HoldingListState,
  action: PayloadAction<{
    colId: ColumnId
    columnState: Partial<ColumnState>
  }>,
) {
  const indexToUpdate = state.config.columnState.findIndex(
    (column) => column.colId === action.payload.colId,
  )
  if (indexToUpdate === -1) {
    return
  }
  // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
  const currentColumnState = state.config.columnState[indexToUpdate]!
  state.config.columnState[indexToUpdate] = {
    ...currentColumnState,
    ...action.payload.columnState,
  }
}

function handleUpdateColumnStates(
  state: HoldingListState,
  action: PayloadAction<{
    columnStates: ColumnState[]
  }>,
) {
  state.config.columnState = action.payload.columnStates
}

function handleSetSelectedBond(
  state: HoldingListState,
  action: PayloadAction<string>,
) {
  state.selectedBond = action.payload
}

function handleSetFilters(
  state: HoldingListState,
  action: PayloadAction<string>,
) {
  // If the filter is the same, don't update the state
  const payloadJson = action.payload ? decodeFilter(action.payload) : null
  if (isEqual(state.filters, payloadJson)) {
    return
  }
  if (!action.payload || action.payload === '') {
    state.filters = null
    state.filterName = null
    return
  }
  state.filters = decodeFilter(action.payload) as FilterObject
}

function handleSetFilterName(
  state: HoldingListState,
  action: PayloadAction<string>,
) {
  state.filterName = action.payload
}

function handleSetSearchTerm(
  state: HoldingListState,
  action: PayloadAction<string>,
) {
  state.searchTerm = action.payload
}

function handleSetQuickFilter(
  state: HoldingListState,
  action: PayloadAction<string>,
) {
  // If the filter is the same, don't update the state
  const payloadJson = action.payload ? decodeFilter(action.payload) : null
  if (isEqual(state.quickFilter, payloadJson)) {
    return
  }
  if (!action.payload || action.payload === '') {
    state.quickFilter = null
    state.quickFilterName = null
    state.quickFilterId = null
    state.interactionMode = FilterInteractionMode.EXPLORING
    return
  }
  state.quickFilter = decodeFilter(action.payload) as FilterObject
  state.interactionMode = FilterInteractionMode.VIEWING
}

function handleSetQuickFilterName(
  state: HoldingListState,
  action: PayloadAction<string>,
) {
  state.quickFilterName = action.payload
}

function handleSetQuickFilterId(
  state: HoldingListState,
  action: PayloadAction<string>,
) {
  state.quickFilterId = action.payload
}

function handleSetSortBy(
  state: HoldingListState,
  action: PayloadAction<string>,
) {
  if (!action.payload || action.payload === '') {
    state.sortBy = null
    return
  }
  const sortBy = action.payload.split(',')
  if (sortBy.length === 0) {
    state.sortBy = null
    return
  }
  const updatedSortBy = []
  for (let i = 0; i < sortBy.length; i++) {
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    const [field, direction] = sortBy[i]!.split('.')
    updatedSortBy.push({
      field: field as PositionSortField,
      direction: direction as SortDirection,
    })
  }
  state.sortBy = updatedSortBy
}

function handleSetPreSelectedAccountAllocations(
  state: HoldingListState,
  action: PayloadAction<
    {
      accountId: string
      momentAccountId?: string
      accountName: string
      quantity: number
      sleeveId?: string
    }[]
  >,
) {
  state.preSelectedAccountAllocations = action.payload
}

function handleClearPreSelectedAccountAllocations(state: HoldingListState) {
  state.preSelectedAccountAllocations = []
}

export const persistHoldingListConfigAsyncThunk = createAsyncThunk(
  // biome-ignore lint/style/useTemplate: Bulk suppression as part of biome migration
  holdingListSlice.reducerPath + '/persistHoldingListConfig',
  async (_data, { dispatch, getState, rejectWithValue }) => {
    const state = (getState() as RootState).holdingList
    try {
      const request: PageConfigRequest = {
        id: Page.HoldingList,
        config: state.config,
        version: state.configVersion,
      }
      await dispatch(updatePageConfig.initiate(request))
    } catch (err) {
      const errorMessage =
        // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
        (err as { response?: { data?: any } }).response?.data ||
        'An unknown error occurred'
      return rejectWithValue(errorMessage)
    }
  },
)

// This allows us to invoke `await dispatch(persistHoldingListAsyncThunk())` in component code so that
// the component is notified when the API request is complete -- something that isn't possible with listener
// Map query param keys to their corresponding slice actions
// Note: 'side' is handled via query params only, not synced to Redux
export const {
  initializeHoldingListConfig,
  resetHoldingListConfig,
  setHoldingListConfig,
  updateColumnStates,
  clearPreSelectedAccountAllocations,
} = holdingListSlice.actions

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default holdingListSlice
